import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { getAdmins, getFilteredAdmins, updateSearchParams } from 'src/store/reducers/adminsSlice';
import { openDrawer } from 'src/store/reducers/drawerSlice';
import { setSelectedRow } from 'src/store/reducers/tableSlice';
import { getSearchParams } from 'src/utils/search';

import {
  AdminDialog,
  EmptyPageState,
  MainPage,
  RightAdminDrawer,
  Table,
  ToggleBox,
} from '../../components';
import { ChevronDown, ChevronUp, SearchIcon } from '../../Icons';
import { showAdminDialog } from '../../store/reducers/dialogSlice';
import { SortByMenuStyleProps } from '../CommonComponentsStyles';

import { columns, paramsCatalogue } from './AdminsScreen.data';
import { SortTypes } from './AdminsScreen.types';

export function AdminsScreen () {
  const dispatch = useAppDispatch();

  const { isAdminDialogOpen } = useAppSelector((state) => state.dialog);
  const { admins, filteredAdmins, searchParams: isSearching } = useAppSelector((state) => state.admins);
  const {
    isAdminsLoading,
    isFilteredAdminsLoading,
    selectedToggleBoxValue: toogleBoxValue,
    searchInputValue: searchValue,
    sortByValue: sortValue,
  } = useAppSelector((state) => state.admins);

  const [selectedToggleBox, setSelectedToggleBox] = useState<string>(toogleBoxValue);
  const [searchInput, setSearchInput] = useState(searchValue);
  const [sortBy, setSortBy] = useState<SortTypes>(sortValue);
  const [isSortSelectOpen, setIsSortSelectOpen] = useState<boolean>(false);

  const handleChange = (nextSelected: string) => {
    setSelectedToggleBox(nextSelected);
  };

  const handleChangeSortBy = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as SortTypes);
    dispatch(updateSearchParams({ selectedToggleBox, searchInput, sortBy }));
    dispatch(getFilteredAdmins(
      getSearchParams(
        paramsCatalogue,
        event.target.value,
        selectedToggleBox,
        searchInput
      )
    ));
  };

  const handleOpenAdminDialog = () => {
    dispatch(showAdminDialog());
  };

  const handleSearchClick = () => {
    dispatch(updateSearchParams({ selectedToggleBox, searchInput, sortBy }));
    dispatch(getFilteredAdmins(
      getSearchParams(
        paramsCatalogue,
        sortBy,
        selectedToggleBox,
        searchInput
      )
    ));
  };

  const onRowClick = (params: any) => {
    dispatch(setSelectedRow(params.row));
    dispatch(openDrawer());
  };

  useEffect(() => {
    dispatch(getAdmins());
  }, [dispatch]);

  return (
    <MainPage
      DialogComp={<AdminDialog isOpen={isAdminDialogOpen} />}
      DrawerComp={<RightAdminDrawer />}
      EmptyStateComp={
        <EmptyPageState
          buttonText={'Add admin'}
          image="/images/empty-page-states/admins.png"
          subTitle="To start managing administrators, add new administrators
          and assign them various roles and responsibilities."
          title="It seems there are no administrators configured in the system."
          onButtonClick={handleOpenAdminDialog}
        />
      }
      addButtonText={'Add admin'}
      isDialogOpen={isAdminDialogOpen}
      isShouldShowEmptyState={!admins.length && !isAdminsLoading}
      title="Admins"
      onAddButtonClick={handleOpenAdminDialog}
    >
      <main className="main-page">
        <Box
          display="flex"
          style={{
            padding: '24px 16px 0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
            }}
          >
            <Box
              sx={{
                border: '1px solid #D6D6DB',
                borderBottomLeftRadius: '8px',
                borderTopLeftRadius: '8px',
                borderRight: 'none',
                display: 'flex',
                alignItems: 'center',
                paddingX: 2,
                paddingY: 1,
                color: '#1F1F1F',
                fontWeight: '500',
              }}
            >
              <ToggleBox
                label="Name"
                selected={selectedToggleBox}
                onSelected={handleChange}
              />
              <ToggleBox
                label="Studio"
                selected={selectedToggleBox}
                style={{
                  marginX: 1,
                }}
                onSelected={handleChange}
              />
              <ToggleBox
                label="Email"
                selected={selectedToggleBox}
                onSelected={handleChange}
              />
            </Box>
            <Box
              display="flex"
              sx={{
                border: '1px solid #D6D6DB',
                backgroundColor: '#FAFAFA',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                flex: 1,
                maxWidth: '432px',
              }}
            >
              <InputBase
                placeholder="Search"
                sx={{ ml: 2, flex: 1, fontSize: '14px' }}
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <IconButton
                type="button"
                onClick={handleSearchClick}
              >
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ minWidth: 120, marginLeft: "16px" }}>
            <FormControl fullWidth>
              <InputLabel id="select-sort-by-label">Sort by</InputLabel>
              <Select
                IconComponent={() => (
                  <>{isSortSelectOpen ? <ChevronUp fontSize="inherit" /> : <ChevronDown fontSize="inherit" />}</>
                )}
                MenuProps={SortByMenuStyleProps}
                id="select-sort-by"
                label="Sort by"
                labelId="select-sort-by-label"
                sx={{
                  borderRadius: '8px',
                  color: '#1F1F1F',
                  fontWeight: '500',
                  height: '48px',

                  '& .MuiSvgIcon-root': {
                    marginRight: '14px',
                    color: isSortSelectOpen ? '#BF295C' : '#676767',
                  },
                }}
                value={sortBy}
                onChange={handleChangeSortBy}
                onClose={() => setIsSortSelectOpen(false)}
                onOpen={() => setIsSortSelectOpen(true)}
              >
                <MenuItem
                  disableRipple
                  value={SortTypes.ID}
                >
                  {SortTypes.ID}
                </MenuItem>
                <MenuItem
                  disableRipple
                  value={SortTypes.NAME}
                >
                  {SortTypes.NAME}
                </MenuItem>
                <MenuItem
                  disableRipple
                  value={SortTypes.STUDIO}
                >
                  {SortTypes.STUDIO}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          style={{
            padding: '32px 16px',
            paddingBottom: admins.length === 0 ? '72px' : undefined,
          }}
        >
          <Table
            columns={columns}
            loading={isAdminsLoading || isFilteredAdminsLoading}
            rows={isSearching ? filteredAdmins : admins}
            onRowClick={onRowClick}
          />
        </Box>
      </main>
    </MainPage>
  );
}
